import { useState, useEffect } from 'react';
import { Card, CardContent } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Progress } from '../../components/ui/progress';
import { Mail, CheckCircle2, Clock, AlertTriangle, Send } from 'lucide-react';

export default function AdminEmailBlast() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const token = localStorage.getItem('funnely_token');
    fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/email-blast`, {
      headers: { Authorization: `Bearer ${token}` }
    })
      .then(r => r.json())
      .then(d => { setData(d); setLoading(false); })
      .catch(() => setLoading(false));
  }, []);

  if (loading) return <div className="space-y-4"><div className="h-10 w-48 bg-muted animate-pulse rounded" /></div>;
  if (!data?.campaign) return <div className="text-center py-12 text-muted-foreground">No active campaign</div>;

  const progress = (data.sent_count / data.total_emails) * 100;

  return (
    <div className="space-y-6" data-testid="admin-email-blast">
      <div>
        <h1 className="text-2xl font-semibold tracking-tight" style={{ color: '#195259' }}>Email Blast</h1>
        <p className="text-sm text-muted-foreground mt-1">Campaign: {data.campaign}</p>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="pt-5 pb-4">
            <div className="flex items-center gap-2 mb-1"><Send className="h-4 w-4 text-[#FF9A00]" /><span className="text-xs text-muted-foreground">Sent</span></div>
            <p className="text-2xl font-bold" style={{ color: '#195259' }}>{data.sent_count}</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-5 pb-4">
            <div className="flex items-center gap-2 mb-1"><Mail className="h-4 w-4 text-[#195259]" /><span className="text-xs text-muted-foreground">Total</span></div>
            <p className="text-2xl font-bold" style={{ color: '#195259' }}>{data.total_emails}</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-5 pb-4">
            <div className="flex items-center gap-2 mb-1"><Clock className="h-4 w-4 text-amber-500" /><span className="text-xs text-muted-foreground">Remaining</span></div>
            <p className="text-2xl font-bold" style={{ color: '#195259' }}>{data.remaining}</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-5 pb-4">
            <div className="flex items-center gap-2 mb-1"><CheckCircle2 className="h-4 w-4 text-emerald-500" /><span className="text-xs text-muted-foreground">Status</span></div>
            <Badge className={data.status === 'active' ? 'bg-emerald-100 text-emerald-700' : 'bg-muted'}>{data.status}</Badge>
          </CardContent>
        </Card>
      </div>

      {/* Progress */}
      <Card>
        <CardContent className="pt-5">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-medium">Progress</span>
            <span className="text-sm text-muted-foreground">{data.sent_count}/{data.total_emails} ({Math.round(progress)}%)</span>
          </div>
          <Progress value={progress} className="h-3" />
          <div className="flex items-center justify-between mt-3 text-xs text-muted-foreground">
            <span>Batch {data.current_batch} of {Math.ceil(data.total_emails / data.batch_size)}</span>
            {data.last_batch_at && <span>Last sent: {new Date(data.last_batch_at).toLocaleString()}</span>}
          </div>
        </CardContent>
      </Card>

      {/* Last Batch Stats */}
      {data.last_batch_at && (
        <Card>
          <CardContent className="pt-5">
            <h3 className="text-sm font-medium mb-3">Last Batch</h3>
            <div className="flex gap-4 text-sm">
              <span className="flex items-center gap-1.5"><CheckCircle2 className="h-3.5 w-3.5 text-emerald-500" /> {data.last_batch_success} sent</span>
              {data.last_batch_fail > 0 && <span className="flex items-center gap-1.5"><AlertTriangle className="h-3.5 w-3.5 text-red-500" /> {data.last_batch_fail} failed</span>}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Sent Emails List */}
      <Card>
        <CardContent className="pt-5">
          <h3 className="text-sm font-medium mb-3">Sent Emails ({data.sent_count})</h3>
          <div className="max-h-[300px] overflow-y-auto space-y-1">
            {data.sent_emails.map((email, i) => (
              <div key={email} className="flex items-center gap-2 py-1.5 px-2 rounded hover:bg-muted/50 text-sm">
                <CheckCircle2 className="h-3.5 w-3.5 text-emerald-500 shrink-0" />
                <span className="text-muted-foreground text-xs w-6">{i + 1}.</span>
                <span className="truncate">{email}</span>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
